import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { Grid, makeStyles, Typography } from "@material-ui/core";

import { ShaDowBox } from "../../components/boxes";
import { COLORS } from "../../theme/colors";
import { Map } from "../../components/map";
import { MAP_LEGENDS, MAP_TABS, KPIS } from "./constants";
import { ActionTimeGroup } from "../../components/actionTimeTimeGroup";
import { BuildingInfo } from "./buildingInfo";
import { Modal } from "../../components/base";
import {
  PowerRevenueKPI,
  EmissionReductionKPI,
  NOIIncreaseKPI,
  UnrealizedPowerRevenueKPI,
} from "./KPIs";
import {
  NOIChart,
  PowerRevenueChart,
  EmissionReductionChart,
  UnrealizedPowerRevenueChart,
} from "./Charts";
import { getData } from "../../services";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: `${theme.spacing(2.5)}px ${theme.spacing(4)}px`,
    height: "100%",
    boxSizing: "border-box",
  },
  kpisContainer: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  mapTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 400,
  },
  mapContainer: {
    padding: `${theme.spacing(1.25)}px ${theme.spacing(0.5)}px`,
  },
  mapTabTitle: {
    marginLeft: theme.spacing(4),
    marginRight: theme.spacing(4),
    marginBottom: theme.spacing(1.25),
    cursor: "pointer",
  },
  timeRangeText: {
    fontWeight: theme.typography.fontWeightRegular,
    textTransform: "none",
  },
}));

const BuildingContent = React.forwardRef((props, ref) => (
  <BuildingInfo {...props} forwardRef={ref} />
));

export const DashboardPage = () => {
  const classes = useStyles();

  const [mapTab, setMapTab] = useState(0);
  const [clickedBuilding, setClickedBuilding] = useState(null);
  const [activeKpi, setActiveKpi] = useState(KPIS.power_revenue);
  const { period, startDate, endDate } = useSelector((state) => state.selectedPeriods);
  const [url, setUrl] = useState(MAP_TABS[0]);
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    if (url) {
      getData(`/praas/v1/dashboard/map/${url.replace(/ /g, "-").toLowerCase()}/`, setProperties);
    }
  }, [url]);

  const getGraph = () => {
    switch (activeKpi) {
      case KPIS.power_revenue:
        return (
          <PowerRevenueChart
            activeKpi={activeKpi}
            period={period}
            startDate={startDate}
            endDate={endDate}
          />
        );
      case KPIS.emission_reduction:
        return (
          <EmissionReductionChart
            activeKpi={activeKpi}
            period={period}
            startDate={startDate}
            endDate={endDate}
          />
        );
      case KPIS.unrealized_power_revenue:
        return (
          <UnrealizedPowerRevenueChart
            activeKpi={activeKpi}
            period={period}
            startDate={startDate}
            endDate={endDate}
          />
        );
      case KPIS.noi_increase:
        return (
          <NOIChart activeKpi={activeKpi} period={period} startDate={startDate} endDate={endDate} />
        );
      default:
        return (
          <PowerRevenueChart
            activeKpi={activeKpi}
            period={period}
            startDate={startDate}
            endDate={endDate}
          />
        );
    }
  };

  return (
    <div className={classes.root}>
      <ActionTimeGroup
        hideShareBtn
        hideExportBtn
        hideIntegrateBtn
        titleSize="h2"
        timeRangeStyle={classes.timeRangeText}
      />

      {/* KPIs part */}
      <Grid className={classes.kpisContainer} container justifyContent="space-between" spacing={4}>
        <PowerRevenueKPI
          activeKpi={activeKpi}
          setActiveKpi={setActiveKpi}
          period={period}
          startDate={startDate}
          endDate={endDate}
        />
        <EmissionReductionKPI
          activeKpi={activeKpi}
          setActiveKpi={setActiveKpi}
          period={period}
          startDate={startDate}
          endDate={endDate}
        />
        <NOIIncreaseKPI
          activeKpi={activeKpi}
          setActiveKpi={setActiveKpi}
          period={period}
          startDate={startDate}
          endDate={endDate}
        />
        <UnrealizedPowerRevenueKPI
          activeKpi={activeKpi}
          setActiveKpi={setActiveKpi}
          period={period}
          startDate={startDate}
          endDate={endDate}
        />
      </Grid>

      {getGraph()}

      {/* Map part */}
      <Typography className={classes.mapTitle} variant="h2">
        Maximizing Portfolio Performance
      </Typography>
      <ShaDowBox>
        <div className={classes.mapContainer}>
          <Grid container>
            {MAP_TABS.map((tab, index) => (
              <Typography
                className={classes.mapTabTitle}
                key={index}
                style={{ color: index === mapTab ? COLORS.purple : undefined }}
                onClick={() => {
                  setMapTab(index);
                  setUrl(tab);
                }}
              >
                {tab}
              </Typography>
            ))}
          </Grid>
          <Map
            properties={properties}
            legends={MAP_LEGENDS[mapTab]}
            setClickedBuilding={setClickedBuilding}
            mapTab={mapTab}
          />
        </div>
      </ShaDowBox>

      {/* Modal when clicking map icons */}
      <Modal open={!!clickedBuilding} onClose={() => setClickedBuilding(null)}>
        <BuildingContent building={clickedBuilding} onClose={() => setClickedBuilding(null)} />
      </Modal>
    </div>
  );
};
